import { useEffect, useRef } from 'react'

const MAX_AUTO_FETCH_PAGES = 15

type UseAutoFetchUntilPostInListParams = {
  selectedPostId: string | undefined
  selectedPost: { resolved: { id: string } } | undefined
  hasNextPage: boolean
  fetchingMore: boolean
  fetchMore: () => Promise<void>
}

/**
 * When the user opens a post by URL (e.g. "Show all comments") and that post
 * isn't in the first page of the list, automatically fetch more pages until
 * the post appears or we hit the page limit.
 */
export function useAutoFetchUntilPostInList({
  selectedPostId,
  selectedPost,
  hasNextPage,
  fetchingMore,
  fetchMore,
}: UseAutoFetchUntilPostInListParams): void {
  const autoFetchCountRef = useRef(0)
  const lastSelectedPostIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (selectedPostId !== lastSelectedPostIdRef.current) {
      lastSelectedPostIdRef.current = selectedPostId
      autoFetchCountRef.current = 0
    }
  }, [selectedPostId])

  useEffect(() => {
    if (
      !selectedPostId ||
      selectedPost ||
      !hasNextPage ||
      fetchingMore ||
      autoFetchCountRef.current >= MAX_AUTO_FETCH_PAGES
    ) {
      return
    }

    autoFetchCountRef.current += 1
    void fetchMore()
  }, [selectedPostId, selectedPost, hasNextPage, fetchingMore, fetchMore])
}
